import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Hasta dónde llega ESTE usuario: sus fincas, menos los sectores que se le hayan
 * quitado.
 *
 * EL ALCANCE SE CONCEDE, NO SE DEDUCE (01-09-2026). Esto leía
 * `block_worker_assignments` y sacaba las fincas de los sectores asignados, y
 * por eso el acceso parecía accidental: el alcance de alguien era un efecto
 * secundario de una asignación de TRABAJO que nadie pensó como un permiso.
 * Tenía además dos vueltas malas:
 *
 *   · un manager SIN sectores se quedaba con acceso a TODO;
 *   · quitarle a alguien su último sector le ENSANCHABA el acceso, en silencio.
 *
 * Y SE CONCEDE POR FINCA; LOS SECTORES SOLO RESTAN (02-09-2026). Antes el
 * alcance de un trabajador era una lista de sectores a marcar uno a uno, y los
 * datos de producción enseñaban lo que eso valía: 205 filas de sector para decir
 * «la finca entera» diez veces y «la finca menos un sector» tres. Peor que
 * aparatoso, era una trampa: un sector NUEVO nacía invisible para todos los
 * trabajadores de esa finca, porque no estaba en su lista. Con exclusiones pasa
 * lo que espera cualquiera —y lo que ya decía la tabla entera con su «sin filas
 * = todas las fincas»—: lo que no se ha quitado, se tiene.
 *
 * SIN FILAS = SIN RECORTE, y esto es deliberado. Es lo que mantiene el valor por
 * defecto de siempre (una empresa que no usa esto no nota nada) y lo que deja
 * fuera a los administradores.
 *
 * Y VALE TAMBIÉN PARA LOS TRABAJADORES, que parece un fallo y no lo es
 * (confirmado por el usuario, 01-09-2026). Un trabajador sin nada concedido ve
 * toda la empresa: en una empresa que no reparte sectores, eso es justo lo que
 * se quiere, y la alternativa —dejarle sin ver nada— vaciaría la aplicación a
 * los doce trabajadores que hoy no tienen ninguno. Quien quiera acotar a
 * alguien, se lo concede; mientras no se le conceda nada, no está acotado. La
 * misma regla vive en SQL —`location_in_manager_scope`, que usa la RLS— para que
 * no haya dos definiciones de «mis fincas» que puedan separarse. Un desplegable
 * recortado es comodidad; la regla de verdad está en la base de datos.
 */
/** Identidad estable para «todavía no lo sé»: ver el comentario de abajo. */
const NADA_TODAVIA: Set<string> = new Set();

export function useLocationScope() {
  const { user, company, role } = useAuth();
  /* El perito trabaja sobre varias empresas desde su propio portal y el admin
     manda en la suya entera: ninguno de los dos se recorta. */
  const recortable = role === "manager" || role === "worker";

  const { data, isLoading } = useQuery({
    queryKey: ["member-scope", company?.id, user?.id],
    enabled: recortable && !!company?.id && !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      /* `as never` sobre el nombre de la tabla: `types.ts` lo genera Lovable y
         todavía no conoce `member_scope`. Es el mismo apaño que ya usa
         `restockProduct` con sus RPC nuevas; en cuanto se regeneren los tipos,
         se puede quitar. */
      const { data: rows, error } = await supabase
        .from("member_scope" as never)
        .select("location_id, block_id, excluded")
        .eq("company_id", company!.id)
        .eq("user_id", user!.id);
      if (error) throw error;
      const filas = (rows ?? []) as unknown as Array<{
        location_id: string | null;
        block_id: string | null;
        excluded: boolean | null;
      }>;

      const locations = new Set<string>();
      const excluded = new Set<string>();
      for (const r of filas) {
        /* Solo CONCEDE la fila de finca entera. Una fila con sector es una
           resta, y contarla como concesión sería justo lo contrario de lo que
           dice. Mismo criterio que `location_in_manager_scope` en SQL. */
        if (r.block_id) {
          if (r.excluded) excluded.add(r.block_id);
        } else if (r.location_id) {
          locations.add(r.location_id);
        }
      }
      return { locations, excluded, hayFilas: filas.length > 0 };
    },
  });

  /* HAY RECORTE EN CUANTO HAY UNA FILA, no en cuanto hay una finca concedida.
     La diferencia importa en un estado que la interfaz no crea pero la base de
     datos permite: exclusiones sin concesión. Mirando las fincas, ese caso
     saldría «sin recorte» y le abriría la empresa entera; mirando las filas sale
     un conjunto vacío y no ve nada, que es el lado seguro y lo mismo que
     responde la RLS. */
  const allowedLocationIds = recortable && data?.hayFilas ? data.locations : null;
  const excludedBlockIds = recortable && data ? data.excluded : null;

  /* MIENTRAS NO SE SABE, NO SE ENSEÑA NADA — y esto es la mitad del problema,
     no un detalle (02-09-2026).
     Saber el alcance cuesta una consulta. Hasta que contesta, `data` es
     `undefined` y lo de arriba da `null`, que significa «sin recorte»: durante
     ese rato TODAS las pantallas enseñan la empresa entera. En una tabla que
     tarda en llegar no se nota; en el Panel, que pinta en cuanto puede, se ve
     perfectamente — y las listas que se piden una sola vez en un `useEffect`
     se quedaban así hasta recargar la página. Buena parte de «Ron sigue viendo
     Campohermoso» era esto y no un filtro que faltara.
     Un conjunto VACÍO mientras se carga enseña de menos durante un instante en
     vez de enseñar de más. Enseñar de menos se corrige solo al llegar el dato;
     enseñar de más ya se ha enseñado.
     El conjunto es una CONSTANTE del módulo a propósito: devolver un `new Set()`
     en cada render cambia la identidad, y todas las pantallas lo llevan en las
     dependencias de sus efectos — sería un bucle de refetch infinito. */
  /* Y SI LA CONSULTA FALLA, se abre. Es la decisión incómoda de aquí: un fallo
     de red dejaría la aplicación vacía a TODO manager y trabajador, también en
     las empresas que no usan el alcance, y eso parece una aplicación rota. Se
     asume porque la frontera de verdad está en la base de datos (la RLS de
     `inventory_location_stock` y las escrituras) y esto es comodidad; si algún
     día las lecturas de tareas y partes tienen RLS propia, esto puede cerrarse
     sin que se note. */
  const cargando = recortable && isLoading;
  const fincas = cargando ? NADA_TODAVIA : allowedLocationIds;

  return {
    /** null = sin recorte; Set con las fincas permitidas cuando aplica. */
    allowedLocationIds: fincas,
    /** Sectores QUITADOS dentro de una finca que sí es suya. Vacío casi siempre. */
    excludedBlockIds,
    isScoped: !!fincas,
    /** Atajo para filtrar: ¿este sector es suyo? Sin sector, decide la finca. */
    enAlcanceSector: (blockId?: string | null) => !blockId || !excludedBlockIds?.has(blockId),
    /** true mientras aún no se sabe el alcance. */
    loading: recortable ? isLoading : false,
  };
}

/**
 * Los invernaderos de sus fincas, para las consultas que solo saben de
 * invernaderos.
 *
 * Analítica y los informes agrupan por `greenhouse_id` y no traen la finca en
 * la fila, así que no hay nada que filtrar después: hay que acotar la CONSULTA.
 * Esto resuelve fincas → invernaderos una vez y lo deja en caché.
 *
 * `null` = sin recorte, igual que `allowedLocationIds`. Un array VACÍO significa
 * «ninguno», que no es lo mismo: hay que distinguirlos antes de montar un
 * `.in(...)`, porque `.in("greenhouse_id", [])` no devuelve nada — que es justo
 * lo correcto para alguien sin fincas, pero un desastre si se confunde con
 * «sin recorte».
 */
export function useAllowedGreenhouseIds(): string[] | null {
  const { company } = useAuth();
  const { allowedLocationIds } = useLocationScope();
  const clave = allowedLocationIds ? [...allowedLocationIds].sort().join(",") : "todas";

  const { data } = useQuery({
    queryKey: ["scope-greenhouses", company?.id, clave],
    enabled: !!company?.id && !!allowedLocationIds,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from("greenhouses")
        .select("id")
        .eq("company_id", company!.id)
        .in("location_id", [...allowedLocationIds!]);
      if (error) throw error;
      return (rows ?? []).map((g) => g.id);
    },
  });

  if (!allowedLocationIds) return null;
  /* Mientras no se sepa, NINGUNO: mismo criterio que arriba — enseñar de menos
     un instante antes que enseñar de más. */
  return data ?? [];
}

/**
 * ¿A quién de la empresa puede VER este usuario?
 *
 * La regla es la MISMA que `/teams` aplica a mano desde el 01-09-2026, y ahora
 * vive en un solo sitio porque son CUATRO las pantallas que la necesitan: la
 * plantilla de Ajustes, el desplegable de «Asignar a», el de los filtros de
 * tareas y el de la tarea por voz. Cuatro copias parecidas se separan solas.
 *
 * Un compañero se ve si NO tiene alcance concedido —pertenece a todas las
 * fincas por definición: un administrador, un perito, o alguien a quien nadie
 * ha acotado— o si comparte al menos una finca con quien mira.
 *
 * ESO DEJA LOS DESPLEGABLES PARCIALMENTE PERMISIVOS, y es a propósito: hoy solo
 * 14 de los trabajadores de la base tienen filas, así que el resto sigue
 * saliendo para todos. Es el contrato de `member_scope` («sin filas = sin
 * recorte»), el mismo que sostiene a las 20 empresas que no reparten fincas;
 * cambiarlo aquí, a mano y en una sola pantalla, vaciaría los desplegables a
 * media plantilla y dejaría tareas sin poder asignar.
 *
 * MIENTRAS NO SE SABE, NO SE ENSEÑA: si quien mira está acotado y todavía no ha
 * llegado el reparto, `esVisible` dice que no y `cargando` dice por qué. Es el
 * mismo criterio que el resto del fichero —enseñar de menos un instante antes
 * que enseñar de más—, pero SOLO funciona si quien llama respeta `cargando` y
 * pinta un «cargando» en vez de una lista vacía. Una lista que aparece llena y
 * encoge un segundo después enseña justo lo que se quería tapar.
 */
export function useCompanerosVisibles() {
  const { company } = useAuth();
  const { allowedLocationIds, loading } = useLocationScope();
  const clave = allowedLocationIds ? [...allowedLocationIds].sort().join(",") : "todas";

  const { data: fincasPorUsuario } = useQuery({
    queryKey: ["companeros-visibles", company?.id, clave],
    /* `!loading` es obligatorio: sin él, la consulta sale con el alcance vacío
       de los primeros milisegundos, react-query cachea ESE resultado bajo la
       clave "todas" y el recorte no llega hasta recargar. */
    enabled: !!company?.id && !loading && !!allowedLocationIds,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from("member_scope")
        .select("user_id, location_id, block_id")
        .eq("company_id", company!.id);
      if (error) throw error;
      const mapa = new Map<string, Set<string>>();
      for (const r of rows ?? []) {
        /* Las filas de SECTOR solo restan (son exclusiones); la finca se
           concede con `block_id` nulo. Contarlas aquí haría «visible» a alguien
           por una finca de la que justamente se le ha quitado un trozo. */
        if (r.block_id || !r.location_id) continue;
        if (!mapa.has(r.user_id)) mapa.set(r.user_id, new Set());
        mapa.get(r.user_id)!.add(r.location_id);
      }
      return mapa;
    },
  });

  const esVisible = useCallback(
    (userId?: string | null) => {
      if (!userId) return true;
      if (!allowedLocationIds) return true;
      if (!fincasPorUsuario) return false;
      const suyas = fincasPorUsuario.get(userId);
      if (!suyas || suyas.size === 0) return true;
      for (const finca of suyas) if (allowedLocationIds.has(finca)) return true;
      return false;
    },
    [allowedLocationIds, fincasPorUsuario],
  );

  return {
    esVisible,
    /** true mientras no se pueda decidir todavía. Quien llame DEBE respetarlo. */
    cargando: loading || (!!allowedLocationIds && !fincasPorUsuario),
  };
}
